import { useState, useEffect, useRef } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import Avatar from "@/components/app/Avatar";
import { formatMonthYear } from "@/lib/timeHelpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  CalendarDays, MapPin, ExternalLink, Settings,
  UserPlus, UserCheck, Clock, Search, Users
} from "lucide-react";
import MyLibrarySection from "@/components/app/MyLibrarySection";
import { EventBadge } from "@/components/app/EventBadge";

export default function ProfileShow() {
  const {
    profile, upcoming, past, is_own_profile, library_games, routes,
    friendship_status, friends_count, incoming_requests, outgoing_requests, friends
  } = usePage().props;
  const { auth } = usePage().props;
  const [tab, setTab] = useState("upcoming");

  const events = tab === "upcoming" ? upcoming : past;

  return (
    <AppLayout>
      <div className="container py-8 max-w-7xl mx-auto">
        {/* Profile header */}
        <div className="flex items-start gap-5 mb-8">
          <Avatar user={profile} size="lg" ring />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold leading-tight">{profile.full_name}</h1>
                {profile.username && (
                  <p className="text-muted-foreground text-sm">@{profile.username}</p>
                )}
                {(profile.city || profile.state) && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {[profile.city, profile.state].filter(Boolean).join(", ")}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Member since {formatMonthYear(new Date(profile.member_since))}
                </p>
                {friends_count > 0 && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Users className="h-3 w-3" />
                    {friends_count} {friends_count === 1 ? "friend" : "friends"}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {is_own_profile ? (
                  <Button asChild variant="outline" size="sm">
                    <Link href={routes.settings}>
                      <Settings className="h-4 w-4" />
                      Edit profile
                    </Link>
                  </Button>
                ) : (
                  auth.authenticated && (
                    <FriendshipButton friendship={friendship_status} profileUserId={profile.id} />
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Friends section (own profile only) */}
        {is_own_profile && (
          <FriendSection
            incomingRequests={incoming_requests}
            outgoingRequests={outgoing_requests}
            friends={friends}
          />
        )}

        {/* Event history */}
        <div className="mb-6">
          <div role="tablist" className="flex gap-1 border-b border-border mb-4">
            <TabButton id="tab-upcoming" controls="tabpanel-events" active={tab === "upcoming"} onClick={() => setTab("upcoming")}>
              Upcoming
              {upcoming.length > 0 && (
                <span className="ml-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium px-1.5 py-0.5">
                  {upcoming.length}
                </span>
              )}
            </TabButton>
            <TabButton id="tab-past" controls="tabpanel-events" active={tab === "past"} onClick={() => setTab("past")}>
              Past
            </TabButton>
          </div>

          <div id="tabpanel-events" role="tabpanel" aria-labelledby={`tab-${tab}`}>
          {events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                {tab === "upcoming"
                  ? "No upcoming events"
                  : "No past events yet"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((participation, i) => (
                <EventCard key={i} participation={participation} />
              ))}
            </div>
          )}
          </div>
        </div>

        {/* BGG section */}
        <div className="rounded-lg border border-border bg-card p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              BoardGameGeek
            </h2>
            {profile.bgg_username && (
              <a
                href={`https://boardgamegeek.com/user/${profile.bgg_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary flex items-center gap-1 hover:underline"
              >
                {profile.bgg_username}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {profile.bgg_username ? (
            <div className="grid grid-cols-3 gap-4 text-center">
              <BggStat label="Collection" value="—" note="Coming soon" />
              <BggStat label="Plays" value="—" note="Coming soon" />
              <BggStat label="H-index" value="—" note="Coming soon" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {is_own_profile
                ? "Add your BGG username in settings to display your collection and play history."
                : "No BGG account linked."}
            </p>
          )}
        </div>

      </div>

      {/* Game Library — full width */}
      <div className="px-4 sm:px-6 pb-8">
        <MyLibrarySection
          games={library_games}
          showSyncButton={is_own_profile}
          showTabs={true}
          canTeachIds={new Set(library_games.filter((g) => g.can_teach).map((g) => g.bgg_id ?? g.bgg_game_id))}
          myCanTeachIds={is_own_profile ? new Set(library_games.filter((g) => g.can_teach).map((g) => g.bgg_id ?? g.bgg_game_id)) : new Set()}
          onCanTeach={is_own_profile ? (bggId) => {
            const isTeaching = library_games.some((g) => (g.bgg_id ?? g.bgg_game_id) === bggId && g.can_teach);
            const visitOpts = { preserveScroll: true, preserveState: true };
            if (isTeaching) {
              router.delete(`${routes.can_teach_library}/${bggId}/can_teach`, visitOpts);
            } else {
              router.post(`${routes.can_teach_library}/${bggId}/can_teach`, {}, visitOpts);
            }
          } : undefined}
        />
      </div>
    </AppLayout>
  );
}

// ─── Friendship button (shown on other user's profile) ────────────────────────

function FriendshipButton({ friendship, profileUserId }) {
  const { routes } = usePage().props;

  const send = () => router.post(routes.friendships, { recipient_id: profileUserId });

  if (!friendship) {
    return (
      <Button variant="outline" size="sm" onClick={send}>
        <UserPlus className="h-4 w-4" />
        Add Friend
      </Button>
    );
  }

  if (friendship.status === "accepted") {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => router.delete(`${routes.friendships}/${friendship.id}`)}
      >
        <UserCheck className="h-4 w-4" />
        Friends
      </Button>
    );
  }

  if (friendship.status === "pending" && friendship.i_am_requester) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => router.delete(`${routes.friendships}/${friendship.id}`)}
      >
        <Clock className="h-4 w-4" />
        Cancel Request
      </Button>
    );
  }

  if (friendship.status === "pending" && !friendship.i_am_requester) {
    return (
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => router.patch(`${routes.friendships}/${friendship.id}`, { status: "accepted" })}
        >
          Accept
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => router.patch(`${routes.friendships}/${friendship.id}`, { status: "declined" })}
        >
          Decline
        </Button>
      </div>
    );
  }

  // declined — allow re-request
  return (
    <Button variant="outline" size="sm" onClick={send}>
      <UserPlus className="h-4 w-4" />
      Add Friend
    </Button>
  );
}

// ─── Friends section (own profile only) ──────────────────────────────────────

function FriendSection({ incomingRequests, outgoingRequests, friends }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 mb-6">
      <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-4">
        Friends
      </h2>

      {incomingRequests.length > 0 && (
        <IncomingRequests requests={incomingRequests} />
      )}

      {outgoingRequests.length > 0 && (
        <OutgoingRequests requests={outgoingRequests} />
      )}

      <FriendsList friends={friends} />
    </div>
  );
}

function IncomingRequests({ requests }) {
  const { routes } = usePage().props;
  return (
    <div className="mb-4">
      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
        Pending requests ({requests.length})
      </p>
      <ul className="space-y-2">
        {requests.map(({ id, user }) => (
          <li key={id} className="flex items-center gap-3">
            <Avatar user={user} size="sm" />
            <Link href={user.path} className="flex-1 text-sm font-medium hover:underline">
              {user.full_name}
              {user.username && (
                <span className="text-muted-foreground font-normal ml-1">@{user.username}</span>
              )}
            </Link>
            <div className="flex gap-1.5">
              <Button
                size="sm"
                onClick={() => router.patch(`${routes.friendships}/${id}`, { status: "accepted" })}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => router.patch(`${routes.friendships}/${id}`, { status: "declined" })}
              >
                Decline
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OutgoingRequests({ requests }) {
  const { routes } = usePage().props;
  return (
    <div className="mb-4">
      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
        Sent requests
      </p>
      <ul className="space-y-2">
        {requests.map(({ id, user }) => (
          <li key={id} className="flex items-center gap-3">
            <Avatar user={user} size="sm" />
            <Link href={user.path} className="flex-1 text-sm font-medium hover:underline">
              {user.full_name}
              {user.username && (
                <span className="text-muted-foreground font-normal ml-1">@{user.username}</span>
              )}
            </Link>
            <Button
              size="sm"
              variant="outline"
              onClick={() => router.delete(`${routes.friendships}/${id}`)}
            >
              Cancel
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FriendsList({ friends }) {
  const { routes } = usePage().props;
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef              = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.length < 3) {
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res  = await fetch(`${routes.user_search}?q=${encodeURIComponent(query)}`, {
          headers: { Accept: "application/json" }
        });
        const data = await res.json();
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const isSearching   = query.length >= 3;
  const displayList   = isSearching ? results : friends;

  return (
    <div>
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Find a user (3 chars min)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      {loading && (
        <p className="text-xs text-muted-foreground py-2">Searching…</p>
      )}

      {!loading && isSearching && results.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">No users found.</p>
      )}

      {!loading && !isSearching && friends.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">No friends yet. Search above to find someone.</p>
      )}

      <ul className="space-y-2">
        {displayList.map((user) => (
          <li key={user.id} className="flex items-center gap-3">
            <Avatar user={user} size="sm" />
            <Link href={user.path} className="flex-1 text-sm font-medium hover:underline">
              {user.full_name}
              {user.username && (
                <span className="text-muted-foreground font-normal ml-1">@{user.username}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function EventCard({ participation }) {
  const { relationship, event } = participation;
  const startsAt = event.starts_at ? new Date(event.starts_at) : null;

  return (
    <Link href={event.paths.show} className="block group">
      <div className="rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/40 hover:shadow-sm transition-all">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="font-medium text-sm group-hover:text-primary transition-colors truncate">
                {event.title}
              </span>
              {event.status === "cancelled" && (
                <Badge variant="destructive" className="text-xs shrink-0">Cancelled</Badge>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              {startsAt && (
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  {startsAt.toLocaleDateString(undefined, {
                    weekday: "short", month: "short", day: "numeric", year: "numeric"
                  })}
                </span>
              )}
              {event.location_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {event.location_name}
                </span>
              )}
            </div>
          </div>

          <EventBadge relationship={participation.relationship} rsvpStatus={participation.rsvp_status} />
        </div>
      </div>
    </Link>
  );
}


function BggStat({ label, value, note }) {
  return (
    <div>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {note && <p className="text-[10px] text-muted-foreground/60 mt-0.5">{note}</p>}
    </div>
  );
}

function TabButton({ id, controls, active, onClick, children }) {
  return (
    <button
      id={id}
      role="tab"
      aria-selected={active}
      aria-controls={controls}
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
